import {
  Button,
  Combobox,
  ErrorContainer,
  Form,
  FormControl,
  FormField,
  FormInput,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  IconRefresh,
} from "@sentricmusic/ui";
import { useTranslation } from "react-i18next";

import countries from "@/data/countries";
import { str } from "@/helpers/str";

import useSettingsForm from "./hooks/useSettingsForm";
import useSettingsMutation from "./hooks/useSettingsMutation";

function Settings() {
  const { t } = useTranslation();

  const { form, profile } = useSettingsForm();

  const {
    handleUpdateSettings,
    handleSetDefaultAccountCode,
    defaultAccountCode,
    hasMultipleAccounts,
    accounts,
    pending,
    mutationErrorMessage,
  } = useSettingsMutation({ form });

  return (
    <div className="flex-1 order-last xl:w-full bg-layer-raised min-h-50 lg:min-h-screen-minus-header">
      <div className="mx-4 my-12 lg:mx-12 xl:mx-20 2xl:mx-40">
        <Heading as="h3" className="mb-8">{t("settings:update-details-subheader")}</Heading>

        <div className="bg-white shadow-sm rounded-lg p-4 md:px-10 md:py-10">
          {hasMultipleAccounts && (
            <div className="mb-4">
              <Combobox
                label="Default login account"
                searchByLabel
                value={defaultAccountCode ?? ""}
                onValueChange={val => handleSetDefaultAccountCode(val === "" ? null : val)}
                placeholder={t("settings:no-default", "None (ask each login)")}
                data={[
                  { label: t("settings:no-default", "None (ask each login)"), value: "" },
                  ...accounts
                    .slice()
                    .sort((a, b) => a.accountname.localeCompare(b.accountname))
                    .map(acc => ({
                      label: `${acc.accountname} (${acc.artistaccountcode})`,
                      value: acc.artistaccountcode,
                    })),
                ]}
              />
            </div>
          )}

          <Form {...form}>
            <form
              onSubmit={(e) => {
                void form.handleSubmit(handleUpdateSettings)(e);
              }}
              className="flex flex-col gap-4"
            >
              <FormInput
                disabled
                type="email"
                label="Email"
                required
                placeholder={str(t("auth:email-placeholder"))}
                value={profile.data?.email ?? ""}
                readOnly
                className="cursor-not-allowed opacity-50"
              />

              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("common:first-name", "First name")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:first-name", "First name"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("common:last-name", "Last name")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:last-name", "Last name"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="addressline1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("common:address-line-1", "Address line 1")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:address-line-1", "Address line 1"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="addressline2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:address-line-2", "Address line 2")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:address-line-2", "Address line 2"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="addressline3"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:address-line-3", "Address line 3")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:address-line-3", "Address line 3"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="towncity"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("common:town-city", "Town / City")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:town-city", "Town / City"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="countystate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("common:county", "County / State")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:county", "County / State"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="postzipcode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("common:postcode", "Post / Zip code")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:postcode", "Post / Zip code"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phonenumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("common:phone-number", "Phone number")}</FormLabel>
                    <FormControl>
                      <FormInput
                        placeholder={str(t("common:phone-number", "Phone number"))}
                        disabled={profile.isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="countrycode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t("other:select-country-placeholder", "Country")}</FormLabel>
                    <FormControl>
                      <Combobox
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={str(t("other:select-country-placeholder", "Select country"))}
                        data={countries.map(option => ({
                          label: str(t(`countries:${option.name}`)),
                          value: option.value,
                        }))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {mutationErrorMessage.length > 0 && (
                <ErrorContainer message={mutationErrorMessage} />
              )}
              {form.formState.errors.root?.message != null && (
                <ErrorContainer message={form.formState.errors.root.message} />
              )}

              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={pending || profile.isLoading}
                  variant="primary"
                  label={str(pending
                    ? t("common:loading", "Loading...")
                    : t("settings:update-button", "Update"))}
                  icon={pending ? <IconRefresh className="animate-spin" /> : undefined}
                />
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}

export default Settings;
